import { ReportDocument } from "@/components/report-print/ReportDocument";
import type {
  ReportColumn,
  ReportDocumentModel,
  ReportTableRow,
} from "@/components/report-print/reportTypes";
import type {
  QuotationExportDocument,
  QuotationExportGroup,
  QuotationExportItem,
} from "@/features/quotation/types";

interface QuotationPrintDocumentProps {
  document: QuotationExportDocument;
  printMode?: "source" | "preview";
  targetLabel?: string;
}

const QUOTATION_COLUMNS: ReportColumn[] = [
  { key: "product", label: "Produto", width: "38%" },
  { key: "supplier", label: "Fornecedor", width: "27%" },
  {
    key: "unit",
    label: "Unidade",
    width: "15%",
    align: "right",
    noWrap: true,
  },
  {
    key: "price",
    label: "Preço",
    width: "20%",
    align: "right",
    noWrap: true,
  },
];

export function buildQuotationReportModel(
  document: QuotationExportDocument,
  options: { targetLabel?: string } = {},
): ReportDocumentModel {
  const groups = document.groups ?? [];
  const targetSubtitle = options.targetLabel ? ` · ${options.targetLabel}` : "";

  return {
    header: {
      brand: "EloLink",
      logoSvg: document.logo_svg,
      documentLabel: "Cotação para clientes",
      title: "Cotação do Dia",
      subtitle: `Lista diária de preços · ${document.status}${targetSubtitle}`,
      dateLabel: document.date,
      dateSubLabel: document.weekday,
    },
    metrics: [
      { label: "Produtos", value: String(document.total_products) },
      { label: "Fornecedores", value: String(document.total_suppliers) },
      {
        label: "Itens Cotados",
        value: String(document.total_items),
        tone: "highlight",
      },
    ],
    metricRows: [
      [
        { label: "Produtos", value: String(document.total_products) },
        { label: "Fornecedores", value: String(document.total_suppliers) },
        {
          label: "Itens Cotados",
          value: String(document.total_items),
          tone: "highlight",
        },
      ],
    ],
    sections: groups.length
      ? groups.map(buildGroupSection)
      : [
          {
            id: "quotation-empty",
            title: "Produtos",
            columns: QUOTATION_COLUMNS,
            rows: [
              {
                id: "quotation-empty-row",
                tone: "muted",
                cells: [
                  {
                    columnKey: "product",
                    value: "Nenhum produto cotado para esta data.",
                  },
                ],
              },
            ],
          },
        ],
    footer: {
      brand: document.responsible.name || "EloLink",
      generatedLabel: `Gerado em ${document.generated_at}`,
      documentNumber: document.number,
    },
    continuationContext: options.targetLabel
      ? `Cotação para clientes · ${options.targetLabel}`
      : "Cotação para clientes",
  };
}

export function QuotationPrintDocument({
  document,
  printMode = "source",
  targetLabel,
}: QuotationPrintDocumentProps) {
  return (
    <ReportDocument
      document={buildQuotationReportModel(document, { targetLabel })}
      printMode={printMode}
    />
  );
}

function buildGroupSection(group: QuotationExportGroup, groupIndex: number) {
  return {
    id: `quotation-group-${groupIndex}`,
    title: group.category,
    subtitle: `${group.items.length} item(ns) cotado(s)`,
    columns: QUOTATION_COLUMNS,
    rows:
      group.items.length > 0
        ? group.items.map((item, itemIndex) =>
            buildQuotationRow(group, item, itemIndex),
          )
        : [emptyRow(group, groupIndex)],
  };
}

function buildQuotationRow(
  group: QuotationExportGroup,
  item: QuotationExportItem,
  itemIndex: number,
): ReportTableRow {
  return {
    id: `quotation-${group.category}-${item.product}-${itemIndex}`,
    density:
      item.product.length > 34 || item.supplier.length > 30
        ? "wrapped"
        : "normal",
    cells: [
      { columnKey: "product", value: item.product, emphasis: true },
      { columnKey: "supplier", value: item.supplier },
      { columnKey: "unit", value: item.unit, align: "right" },
      {
        columnKey: "price",
        value: item.price,
        secondary: item.price_secondary_label,
        align: "right",
        emphasis: true,
      },
    ],
  };
}

function emptyRow(
  group: QuotationExportGroup,
  groupIndex: number,
): ReportTableRow {
  return {
    id: `quotation-${group.category}-${groupIndex}-empty`,
    tone: "muted",
    cells: [
      {
        columnKey: "product",
        value: "Nenhum produto cotado nesta categoria.",
      },
    ],
  };
}
